<template>
  <tab-list
      v-model="currentTab"
      :tabs-enum="tabs"
      :title="title"
      :loading="isLoading"
      :default-tab="defaultTab"
      @change="changeTab">
    <template v-slot:tabItem>
      <general-error v-if="!!generalError" />
      <v-progress-linear
          :active="isLoading"
          color="loader"
          indeterminate />
      <v-row v-if="showGeneralInfo" class="text-right">
        <v-col class="text-right">
          <btn-icon-tip
              v-if="showGeneralInfo"
              :id="`getRegCard-${organizationId}`"
              icon="description"
              :tip="$t('Скачать регистрационную карточку')"
              @click="getRegistrationCard" />
        </v-col>
      </v-row>
      <organization-info-view
          v-if="showGeneralInfo"
          :organization-info="organization" />
      <organization-users
          v-if="showUsers"
          :organization-id="organizationId" />
      <identity-document-list-view
          v-if="showDocuments"
          :request-files="requestFiles"
          :document-types="requiredDocumentTypes"
          :loading="isLoading" />
      <contract-view
          v-if="showContract"
          :loading="isLoading"
          :contract="contract"
          :organization-id="organizationId"
          :edited="initialize" />
      <bank-accounts-list
          v-if="showBankAccounts"
          :bank-accounts="bankAccounts"
          :organization-id="organizationId"
          can-status
          can-edit
          can-create
          show-status
          :loading="isLoading"
          :edit-bank-account="editBankAccount"
          :create-bank-account="createBankAccount"
          :change-bank-account-status="changeBankAccountStatus" />
    </template>
  </tab-list>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import OrganizationInfoView from '@/components/reused/organization/OrganizationInfoView.vue'
  import ContractView from '@/components/reused/organization/contract/ContractView.vue'
  import BankAccountsList from '@/components/reused/bankAccounts/BankAccountsList.vue'
  import IdentityDocumentListView from '@/components/reused/files/IdentityDocumentListView.vue'
  import OrganizationUsers from './OrganizationUsers.vue'
  import TabList from '@/components/reused/ui/TabList.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'

  import organizationMixin from '@/utils/mixins/scoped/organizationMixin.js'

  import { ORGANIZATION_TABS_ADMIN_ENUM } from '@/constants/enums'
  import {
    ORGANIZATION_TABS
  } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { ORGANIZATION_MANAGEMENT, IDENTITY_FILES, BANK_ACCOUNTS_MANAGEMENT } from '@/constants/store.js'

  export default {
    components: {
      TabList,
      BtnIconTip,
      OrganizationInfoView,
      IdentityDocumentListView,
      OrganizationUsers,
      GeneralError,
      BankAccountsList,
      ContractView
    },
    mixins: [
      organizationMixin
    ],
    props: {
      organizationId: {
        type: String,
        required: true
      },
      tab: {
        type: String
      }
    },
    data () {
      return {
        isLoading: false,
        currentTab: this.tab || ORGANIZATION_TABS.GENERAL_INFO,
        defaultTab: ORGANIZATION_TABS.GENERAL_INFO
      }
    },
    computed: {
      ...mapState(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        organization: state => (state[ORGANIZATION_MANAGEMENT.STATE.CURRENT_ORGANIZATION] || { })
      }),
      ...mapState(IDENTITY_FILES.NAMESPACE, {
        requiredDocumentTypes: state => (state[IDENTITY_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      requestFiles () {
        return this.organization.files || []
      },
      bankAccounts () {
        return this.organization.bankAccounts || []
      },
      contract () {
        return this.organization.contract || {}
      },
      tabs () {
        const tabs = { ...ORGANIZATION_TABS_ADMIN_ENUM }

        return tabs
      },
      requestType () {
        return this.getRequestTypeByOrganization(
          this.organization.organizationType,
          this.organization.participantType,
          this.organization.isEIO)
      },
      showGeneralInfo () {
        return this.currentTab === ORGANIZATION_TABS.GENERAL_INFO
      },
      showUsers () {
        return this.currentTab === ORGANIZATION_TABS.USERS
      },
      showDocuments () {
        return this.currentTab === ORGANIZATION_TABS.DOCUMENTS
      },
      showContract () {
        return this.currentTab === ORGANIZATION_TABS.CONTRACT
      },
      showBankAccounts () {
        return this.currentTab === ORGANIZATION_TABS.BANK_ACCOUNTS
      },
      title () {
        return `${this.$t('Просмотр организации')} ${this.organization.organizationShortName}`
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        if (!vm.organizationId) {
          return
        }
        vm.initialize()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(ORGANIZATION_MANAGEMENT.NAMESPACE, {
        getOrganizationAdminAction: ORGANIZATION_MANAGEMENT.ACTIONS.GET_ORGANIZATION_ADMIN,
        getRegistrationCardAction: ORGANIZATION_MANAGEMENT.ACTIONS.GET_REGISTRATION_CARD,
        clearOrganizationAdminAction: ORGANIZATION_MANAGEMENT.ACTIONS.CLEAR_ORGANIZATION_ADMIN
      }),
      ...mapActions(BANK_ACCOUNTS_MANAGEMENT.NAMESPACE, {
        createBankAccountAction: BANK_ACCOUNTS_MANAGEMENT.ACTIONS.CREATE_BANK_ACCOUNT,
        changeBankAccountStatusAction: BANK_ACCOUNTS_MANAGEMENT.ACTIONS.CHANGE_BANK_ACCOUNT_STATUS,
        updateBankAccountAction: BANK_ACCOUNTS_MANAGEMENT.ACTIONS.UPDATE_BANK_ACCOUNT
      }),
      ...mapActions(IDENTITY_FILES.NAMESPACE, {
        getRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.GET_REQUIRED_DOCUMENT_TYPES,
        clearRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.CLEAR_REQUIRED_DOCUMENT_TYPES
      }),
      changeTab: function (tabValue) {
        this.replaceCurrentRoute(this.getRoute(tabValue))
      },
      getRoute: function (tabValue) {
        return `${this.urlRootPath}/organization/${this.organizationId}/${tabValue}`
      },
      clearStore: async function () {
        await Promise.all(
          [
            this.clearOrganizationAdminAction(),
            this.clearRequiredDocumentTypesAction()
          ])
      },
      initialize: async function () {
        this.isLoading = true
        try {
          await this.getOrganizationAdminAction({
            organizationId: this.organizationId
          })
          await this.getRequiredDocumentTypesAction({
            requestType: this.requestType
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      getRegistrationCard: async function () {
        try {
          this.isLoading = true
          await this.getRegistrationCardAction({
            organizationId: this.organizationId
          })
        } catch (error) {
          this.throwIfNotServerError(error)
        } finally {
          this.isLoading = false
        }
      },
      createBankAccount: async function (model) {
        model.organizationId = this.organizationId
        try {
          this.isLoading = true
          await this.createBankAccountAction(model)
          await this.getOrganizationAdminAction(model)
        } finally {
          this.isLoading = false
        }
      },
      editBankAccount: async function (bankAccount) {
        try {
          const model = {
            bankAccount,
            accountType: bankAccount.accountType,
            bankAccountId: bankAccount.bankAccountId,
            organizationId: this.organizationId
          }
          this.isLoading = true
          await this.updateBankAccountAction(model)
          await this.getOrganizationAdminAction(model)
        } finally {
          this.isLoading = false
        }
      },
      changeBankAccountStatus: async function (bankAccount) {
        try {
          const model = {
            accountType: bankAccount.accountType,
            bankAccountId: bankAccount.bankAccountId,
            organizationId: this.organizationId,
            isActive: bankAccount.isActive
          }
          this.isLoading = true
          await this.changeBankAccountStatusAction(model)
          await this.getOrganizationAdminAction(model)
        } catch (error) {
          this.throwIfUnknownError(error)
        } finally {
          this.isLoading = false
        }
      }
    }
  }
</script>
